'use client'

import { useState } from 'react'
import { useRouter } from '@/shared/navigation'
import { ClientRedirect } from '@/shared/components/ClientRedirect'
import { loginKitchenPin } from '@/features/auth/api/pinLogin'
import { SwitchDeviceFooter } from '@/features/auth/SwitchDeviceFooter'
import {
  formatPinLoginError,
  isDeviceRevokedError,
  PIN_LOGIN_COPY,
} from '@/features/auth/pinLoginErrors'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'
import { getKitchenDevice, hasKitchenDevice, clearKitchenDevice } from '@/shared/auth/deviceStorage'
import { clearDeviceUid } from '@/shared/auth/deviceUid'
import { clearKitchenSession, hasKitchenSession, setKitchenSession } from '@/shared/auth/sessionStorage'
import { useClientHydrated } from '@/shared/hooks/useClientHydrated'

type Props = {
  redirectTo: string
  requireDevice?: boolean
}

export function PinLoginPage({ redirectTo, requireDevice }: Props) {
  const router = useRouter()
  const hydrated = useClientHydrated()
  const [pin, setPin] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [shakeKey, setShakeKey] = useState(0)

  if (hydrated && requireDevice && !hasKitchenDevice()) {
    return <ClientRedirect href="/kitchen/activate" />
  }
  if (hydrated && hasKitchenSession()) {
    return <ClientRedirect href={redirectTo} />
  }

  const device = hydrated ? getKitchenDevice() : null
  const maxDigits = 6
  const canSubmit = pin.length === maxDigits && !isSubmitting

  function resetLocalDevice() {
    clearKitchenSession()
    clearKitchenDevice()
    clearDeviceUid()
  }

  function addDigit(digit: string) {
    if (isSubmitting) return
    setPin((prev) => (prev.length >= maxDigits ? prev : `${prev}${digit}`))
    setError(null)
  }

  function removeDigit() {
    if (isSubmitting) return
    setPin((prev) => prev.slice(0, -1))
    setError(null)
  }

  function handleRevokedDevice() {
    resetLocalDevice()
    router.replace('/kitchen/activate')
  }

  async function submitPin() {
    if (!device || !canSubmit) return
    setIsSubmitting(true)
    setError(null)
    try {
      const session = await loginKitchenPin({
        device_id: device.deviceId,
        pin,
      })
      setKitchenSession(session)
      router.replace(redirectTo)
    } catch (err) {
      const message = err instanceof Error ? err.message : PIN_LOGIN_COPY.loginFailed
      if (isDeviceRevokedError(message)) {
        handleRevokedDevice()
        return
      }
      setError(formatPinLoginError(message))
      setPin('')
      setShakeKey((prev) => prev + 1)
    } finally {
      setIsSubmitting(false)
    }
  }

  const keypadDigits = ['1', '2', '3', '4', '5', '6', '7', '8', '9']
  return (
    <main className="w-full max-w-[480px]">
      <div className="bg-surface-card rounded-xl border border-outline-variant shadow-sm overflow-hidden flex flex-col">
        <div className="bg-surface pt-8 pb-6 px-gutter-tablet sm:px-gutter-desktop flex flex-col items-center border-b border-surface-variant">
          <div className="w-16 h-16 rounded-full bg-primary-container flex items-center justify-center mb-4 text-on-primary-container shadow-sm border border-outline-variant/30">
            <span className="material-symbols-outlined text-[32px] icon-fill">skillet</span>
          </div>
          {device && (
            <p className="text-label-md text-on-surface-variant mb-2 text-center">
              {device.kitchenName} · {device.deviceName}
            </p>
          )}
          <h1 className="text-headline-md text-primary mb-1 text-center">{kitchenTerms.login.title}</h1>
          <p className="text-body-sm text-on-surface-variant text-center">{kitchenTerms.login.subtitle}</p>
        </div>

        <div className="p-gutter-tablet sm:p-gutter-desktop flex flex-col gap-5">
          <div key={shakeKey} className="flex justify-center gap-2 pin-shake" aria-label="PIN indicator">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className={`w-3 h-3 rounded-full border border-outline ${
                  i < pin.length ? 'bg-primary border-primary' : 'bg-surface'
                }`}
              />
            ))}
          </div>

          <div className={`grid grid-cols-3 gap-3 transition-opacity ${isSubmitting ? 'opacity-60' : 'opacity-100'}`}>
            {keypadDigits.map((digit) => (
              <button
                key={digit}
                type="button"
                disabled={isSubmitting}
                onClick={() => addDigit(digit)}
                className="pin-key"
                aria-label={`Angka ${digit}`}
              >
                {digit}
              </button>
            ))}
            <button
              type="button"
              disabled={isSubmitting}
              onClick={removeDigit}
              className="pin-key"
              aria-label="Hapus digit terakhir"
            >
              <span className="material-symbols-outlined text-xl">backspace</span>
            </button>
            <button
              type="button"
              disabled={isSubmitting}
              onClick={() => addDigit('0')}
              className="pin-key"
              aria-label="Angka 0"
            >
              0
            </button>
            <div />
          </div>

          {error && (
            <div role="alert" className="alert-error">
              <span className="material-symbols-outlined text-base shrink-0">error</span>
              <span>{error}</span>
            </div>
          )}
          {!error && pin.length < maxDigits && (
            <p className="text-body-sm text-on-surface-variant text-center">{kitchenTerms.login.helper}</p>
          )}

          <button
            type="button"
            disabled={!canSubmit}
            onClick={submitPin}
            className="btn-cta btn-tactile"
          >
            {isSubmitting ? kitchenTerms.login.submitting : kitchenTerms.login.submit}
          </button>
        </div>

        <SwitchDeviceFooter activateHref="/kitchen/activate" onReset={resetLocalDevice} />
      </div>
    </main>
  )
}
